import { NavBar } from "components/general/NavBar";
import { Footer } from "components/general/Footer";
import { Row, Col, Container, Button, Spinner } from 'react-bootstrap';
import { createContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';

import MeetingCard from "components/Meeting/MeetingCard";
import AgendaComponent from "components/Meeting/AgendaComponent";
import Sidebar from "components/Meeting/Sidebar";

export const MeetingContext = createContext();

export default function Meeting() {
  const params = useParams();
  const url = process.env.REACT_APP_URL;
  const meetingId = params.meetingId;

  const [meeting, setMeeting] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPrevMeeting, setHasPrevMeeting] = useState(false);
  const [hasNextMeeting, setHasNextMeeting] = useState(false);
  const [prevMeetingId, setPrevMeetingId] = useState('');
  const [nextMeetingId, setNextMeetingId] = useState('');
  const [agenda, setAgenda] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const getMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meeting/${meetingId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setMeeting(data);
        // console.log('meeting: ', data);
        setAgenda(data.items);
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load the meeting. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId]);

  const getPreviousMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetings/previous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: meeting?.meetingDetails?.meetingDate }),
      });
      if (response.status === 404) {
        setHasPrevMeeting(false);
        return null;
      }
      if (response.ok) {
        const data = await response.json();
        setPrevMeetingId(data._id);
        setHasPrevMeeting(true);
      } else {
        const errorData = await response.json();
        if (errorData.Error) {
          // toast.error(`Unable to load previous meeting. Error: ${errorData.Error}`, { position: 'bottom-left' });
        } else {
          // toast.error(`Unable to load previous meeting. Error: ${response.statusText}`, { position: 'bottom-left' });
        }
      }
    } catch (error) {
      toast.error(`Error loading previous meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meeting?.meetingDetails?.meetingDate]);

  const getNextMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetings/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: meeting?.meetingDetails?.meetingDate }),
      });
      if (response.status === 404) {
        setHasNextMeeting(false);
        return null;
      }
      if (response.ok) {
        const data = await response.json();
        setNextMeetingId(data._id);
        setHasNextMeeting(true);
      } else {
        const errorData = await response.json();
        if (errorData.Error) {
          // toast.error(`Unable to load next meeting. Error: ${errorData.Error}`, { position: 'bottom-left' });

        } else {
          // toast.error(`Unable to load next meeting. Error: ${response.statusText}`, { position: 'bottom-left' });
        }
      }
    } catch (error) {
      toast.error(`Error loading next meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meeting?.meetingDetails?.meetingDate]);

  const getRoleAssignment = useCallback(async () => {
    try {
      const response = await fetch(`${url}/roleAssignments/${meetingId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const roleAssignment = await response.json();
        setAssignments(roleAssignment);
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load role assignments. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading role assignments: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getMeeting();
      await getRoleAssignment();
      setIsLoading(false);
    };

    fetchData();
  }, [getMeeting, getRoleAssignment]);

  useEffect(() => {
    if (meeting.meetingDetails) {
      getPreviousMeeting();
      getNextMeeting();
    }
  }, [meeting, getPreviousMeeting, getNextMeeting]);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <section className="meeting">
        <Container>
          <Row className="mt-3 mb-3">
            <Col>
              {hasPrevMeeting && (
                <Button variant="success" href={`/meeting/${prevMeetingId}`}>
                  <i className="bi bi-chevron-left"></i> Previous Meeting
                </Button>
              )}
            </Col>
            <Col className="text-end">
              {hasNextMeeting && (
                <Button variant="success" href={`/meeting/${nextMeetingId}`}>
                  Next Meeting <i className="bi bi-chevron-right"></i>
                </Button>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <MeetingCard
                meeting={meeting}
                getMeeting={getMeeting}
                assignments={assignments}
                getRoleAssignment={getRoleAssignment}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <AgendaComponent getMeeting={getMeeting} meeting={meeting} />
            </Col>
          </Row>
        </Container>
      </section>
      <Footer />
      <Sidebar getMeeting={getMeeting} getRoleAssignment={getRoleAssignment} assignments={assignments} agenda={agenda} />
    </>
  );
}